// src/trainingBlocks/index.js  →  dist/trainingBlocks.js  (global: TrainingBlocks)
//
// Draws the pictures and the along-the-way questions (v132.39).
//
// ONE renderer, loaded by BOTH apps, because a Head Office preview that does
// not look like the till is a preview of nothing. admin.html and index.html
// both load this bundle, and both call the same two functions.
//
// Everything here is drawn from DATA. Nothing the model wrote is inserted as
// markup: every string goes through esc() and every shape is decided by this
// file. That is why the blocks live in their own columns rather than in
// body_html, whose attributes are all stripped by the sanitiser.
//
// ON RTL. The blocks take their direction from the caller, because Arabic
// reverses the reading order and a "before to after" arrow pointing the
// wrong way is worse than no arrow. The only thing that flips is the arrow
// glyph and the flex direction; the content order stays as written.

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// v136 dark mode: the palette is CSS variables (shift-ui.css), so a block turns dark with the page. keyFill is the
// blue disc behind the white step number: a brand fill, the same in both themes.
var C = {
  ink: 'var(--text)', sub: 'var(--sub)', line: 'var(--border)', card: 'var(--card)',
  good: 'var(--ok-deep)', goodBg: 'var(--ok-bg)', goodLine: 'var(--learn-ok-line)',
  bad: 'var(--danger-deep)', badBg: 'var(--danger-bg)', badLine: 'var(--learn-bad-line)',
  key: 'var(--info-deep)', keyFill: '#1d4ed8', keyBg: 'var(--info-bg)', keyLine: 'var(--learn-info-line)',
  warmBg: 'var(--warn-bg)', warmLine: 'var(--learn-warn-line)', warm: 'var(--warn-deep)',
};

function shell(title, inner, extra) {
  return '<div style="border:1px solid ' + C.line + ';border-radius:12px;padding:10px 12px;margin:12px 0;background:' + C.card + ';' + (extra || '') + '">' +
    (title ? '<div style="font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;color:' + C.sub + ';margin-bottom:8px">' + esc(title) + '</div>' : '') +
    inner + '</div>';
}

// ── steps: a numbered strip. Order is the whole message, so the numbers are
// large and the line between them is drawn, not implied.
function steps(b) {
  var items = (b.items || []).map(function (t, i) {
    return '<li style="display:flex;align-items:flex-start;gap:10px;margin-bottom:8px">' +
      '<span style="flex:0 0 26px;height:26px;border-radius:50%;background:' + C.keyFill + ';color:#fff;font-weight:800;font-size:13px;display:flex;align-items:center;justify-content:center">' + (i + 1) + '</span>' +
      '<span style="font-size:15px;line-height:1.45;padding-top:3px">' + esc(t) + '</span>' +
    '</li>';
  }).join('');
  return shell(b.title || 'Step by step', '<ol style="list-style:none;margin:0;padding:0">' + items + '</ol>');
}

// ── do and do not: side by side, because the contrast IS the teaching. Two
// separate lists a screen apart do not teach the same thing.
function dodont(b) {
  var col = function (label, arr, colour, bg, line) {
    return '<div style="flex:1 1 180px;min-width:0;background:' + bg + ';border:1px solid ' + line + ';border-radius:10px;padding:8px 10px">' +
      '<div style="font-size:13px;font-weight:800;color:' + colour + ';margin-bottom:4px">' + esc(label) + '</div>' +
      '<ul style="margin:0;padding-inline-start:16px">' + (arr || []).map(function (t) {
        return '<li style="font-size:14.5px;line-height:1.45;margin-bottom:3px">' + esc(t) + '</li>';
      }).join('') + '</ul></div>';
  };
  return shell(b.title || '', '<div style="display:flex;flex-wrap:wrap;gap:10px">' +
    col('✅ Do this', b.do, C.good, C.goodBg, C.goodLine) +
    col('⛔ Not this', b.dont, C.bad, C.badBg, C.badLine) + '</div>');
}

// ── before and after. The arrow carries the direction, so it flips in RTL.
function compare(b, rtl) {
  var panel = function (label, text, colour, bg, line) {
    return '<div style="flex:1 1 180px;min-width:0;background:' + bg + ';border:1px solid ' + line + ';border-radius:10px;padding:8px 10px">' +
      '<div style="font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.3px;color:' + colour + '">' + esc(label) + '</div>' +
      '<div style="font-size:14.5px;line-height:1.45;margin-top:3px">' + esc(text) + '</div></div>';
  };
  var arrow = '<div style="flex:0 0 auto;align-self:center;font-size:20px;color:' + C.sub + '">' + (rtl ? '←' : '→') + '</div>';
  return shell(b.title || '', '<div style="display:flex;flex-wrap:wrap;gap:10px;align-items:stretch">' +
    panel(b.left_label || 'Before', b.left, C.bad, C.badBg, C.badLine) + arrow +
    panel(b.right_label || 'After', b.right, C.good, C.goodBg, C.goodLine) + '</div>');
}

// ── the numbers that matter, large and on their own. A number buried in a
// paragraph is a number nobody remembers.
function facts(b) {
  var cells = (b.items || []).map(function (f) {
    return '<div style="flex:1 1 120px;min-width:0;text-align:center;background:' + C.keyBg + ';border:1px solid ' + C.keyLine + ';border-radius:10px;padding:10px 8px">' +
      '<div style="font-size:21px;font-weight:800;color:' + C.key + ';line-height:1.15">' + esc(f.value) + '</div>' +
      '<div style="font-size:12.5px;color:' + C.sub + ';margin-top:2px;line-height:1.35">' + esc(f.label) + '</div></div>';
  }).join('');
  return shell(b.title || 'Remember', '<div style="display:flex;flex-wrap:wrap;gap:8px">' + cells + '</div>');
}

// One block. An unknown type draws nothing rather than guessing.
function blockHtml(b, rtl) {
  if (!b || !b.type) return '';
  if (b.type === 'steps') return steps(b);
  if (b.type === 'dodont') return dodont(b);
  if (b.type === 'compare') return compare(b, rtl);
  if (b.type === 'facts') return facts(b);
  return '';
}

function blocksHtml(list, rtl) {
  return (Array.isArray(list) ? list : []).map(function (b) { return blockHtml(b, rtl); }).join('');
}

// ── a question along the way. The answer is HIDDEN until asked for, which
// is the entire point: an answer on show is a sentence to skim, and a
// question you actually try to answer is the thing that makes it stick.
function checkpointHtml(c, i) {
  if (!c || !c.q) return '';
  var id = 'tcp' + (i == null ? Math.floor(Math.random() * 1e6) : i);
  return '<details id="' + esc(id) + '" style="background:' + C.warmBg + ';border:1px solid ' + C.warmLine + ';border-radius:12px;padding:10px 12px;margin:12px 0">' +
    '<summary style="cursor:pointer;font-size:15px;font-weight:700;color:' + C.warm + '">\u{1F914} ' + esc(c.q) + '</summary>' +
    '<div style="font-size:11.5px;color:' + C.sub + ';margin:6px 0 4px">Have a think first. This one is not marked.</div>' +
    '<div style="font-size:15px;line-height:1.5">' + esc(c.a) + '</div>' +
  '</details>';
}

function checkpointsHtml(list) {
  return (Array.isArray(list) ? list : []).map(checkpointHtml).join('');
}

// ── the whole body, with everything in its place ───────────────────────────
//
// Walks the body's headings and drops each section's pictures and question
// in after it. Adjacency is not a nicety: a diagram away from the words it
// explains costs a reader more than it gives, which is the one thing the
// research on this is clear about.
//
// Anything whose heading did not match renders at the end rather than being
// thrown away. A picture in the wrong place still beats a missing one, and
// Head Office is warned about it on the admin screen.
function render(bodyHtml, blocks, checkpoints, opts) {
  var o = opts || {};
  var rtl = !!o.rtl;
  var body = String(bodyHtml == null ? '' : bodyHtml);
  var bs = Array.isArray(blocks) ? blocks.slice() : [];
  var cs = Array.isArray(checkpoints) ? checkpoints.slice() : [];
  if (!bs.length && !cs.length) return body;

  var norm = function (x) {
    return String(x == null ? '' : x).replace(/[^\p{L}\p{N} ]/gu, '').replace(/\s+/g, ' ').trim().toLowerCase();
  };
  var matches = function (heading, want) {
    var a = norm(heading); var b = norm(want);
    if (!a || !b) return false;
    return a === b || a.indexOf(b) !== -1 || b.indexOf(a) !== -1;
  };

  // Split on headings, keeping them. Each piece is a section.
  var parts = body.split(/(<h[23][^>]*>[\s\S]*?<\/h[23]>)/i);
  if (parts.length < 2) return body + blocksHtml(bs, rtl) + checkpointsHtml(cs);

  var usedB = {}; var usedC = {};
  var out = '';
  var current = null;
  for (var i = 0; i < parts.length; i++) {
    var piece = parts[i];
    if (/^<h[23]/i.test(piece)) {
      // close the previous section first
      if (current !== null) out += extrasFor(current);
      current = piece.replace(/<[^>]*>/g, ' ');
      out += piece;
    } else {
      out += piece;
    }
  }
  if (current !== null) out += extrasFor(current);

  // whatever matched nothing, at the end
  var restB = bs.filter(function (x, k) { return !usedB[k]; });
  var restC = cs.filter(function (x, k) { return !usedC[k]; });
  out += blocksHtml(restB, rtl) + checkpointsHtml(restC);
  return out;

  function extrasFor(heading) {
    var html = '';
    bs.forEach(function (b, k) {
      if (usedB[k] || !matches(heading, b.after_heading)) return;
      usedB[k] = 1; html += blockHtml(b, rtl);
    });
    cs.forEach(function (c, k) {
      if (usedC[k] || !matches(heading, c.after_heading)) return;
      usedC[k] = 1; html += checkpointHtml(c, k);
    });
    return html;
  }
}

// A plain count for the admin screen, so Head Office can see at a glance
// what the session got without reading the whole thing.
function summary(blocks, checkpoints) {
  var b = Array.isArray(blocks) ? blocks : [];
  var c = Array.isArray(checkpoints) ? checkpoints : [];
  var kinds = {};
  b.forEach(function (x) { kinds[x.type] = (kinds[x.type] || 0) + 1; });
  var bits = Object.keys(kinds).map(function (k) { return kinds[k] + ' ' + k; });
  return (b.length ? b.length + ' picture' + (b.length === 1 ? '' : 's') + ' (' + bits.join(', ') + ')' : 'no pictures')
    + ' · ' + (c.length ? c.length + ' question' + (c.length === 1 ? '' : 's') + ' along the way' : 'no questions along the way');
}

window.TrainingBlocks = {
  render: render, blocksHtml: blocksHtml, blockHtml: blockHtml,
  checkpointsHtml: checkpointsHtml, checkpointHtml: checkpointHtml, summary: summary,
};

export { render, blocksHtml, blockHtml, checkpointsHtml, checkpointHtml, summary };
